import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Alert, AlertDescription } from '../ui/alert';
import { Trophy, Fish, Lock } from 'lucide-react';
import { formatDateShort } from '../../utils/formatters';

const EventResultsTab = ({
  competitions,
  eventSessions,
  selectedCompetition,
  onSelectCompetition,
  onFetchRunningOrder,
  t,
  language
}) => {
  return (
    <Card className="bg-gradient-to-br from-blue-800/30 to-teal-800/30 border-blue-700/20 backdrop-blur-sm">
      <CardHeader>
        <CardTitle className="text-teal-200 flex items-center text-2xl">
          <Trophy className="mr-3" size={24} />
          {t('eventResultsRankings') || 'Event Results & Rankings'}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-blue-200 mb-6">{t('viewFinalResults') || 'View final results and rankings for completed and closed competitions.'}</p>
        
        {/* Competition Selector */}
        <div className="mb-6">
          <label className="text-blue-200 font-semibold mb-2 block">{t('selectCompetition') || 'Select Competition'}</label>
          <Select onValueChange={(compId) => {
            const comp = competitions.find(c => c.id === compId);
            if (comp) onSelectCompetition(comp);
          }}>
            <SelectTrigger className="bg-blue-800/50 border-blue-600 text-white" data-testid="results-competition-select">
              <SelectValue placeholder={t('chooseCompetition') || 'Choose a competition'} />
            </SelectTrigger>
            <SelectContent>
              {competitions.map(comp => (
                <SelectItem key={comp.id} value={comp.id}>
                  {comp.name} - {formatDateShort(comp.start_date, language)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Event Sessions grouped by Event Type — include completed AND closed */}
        {selectedCompetition && (() => {
          const filteredSessions = eventSessions.filter(s =>
            s.competition_id === selectedCompetition.id &&
            (s.status === 'completed' || s.status === 'closed')
          );
          if (filteredSessions.length === 0) {
            return (
              <Alert className="bg-blue-900/30 border-blue-700/50">
                <AlertDescription className="text-blue-200">
                  {t('noCompletedEvents') || 'No completed or closed events found for this competition.'}
                </AlertDescription>
              </Alert>
            );
          }

          const grouped = filteredSessions.reduce((acc, session) => {
            const eventType = session.event_type_code;
            if (!acc[eventType]) acc[eventType] = [];
            acc[eventType].push(session);
            return acc;
          }, {});

          return (
            <div className="space-y-6">
              {Object.entries(grouped).map(([eventType, sessions]) => (
                <Card key={eventType} className="bg-gradient-to-r from-purple-900/40 to-indigo-900/40 border-purple-700/30">
                  <CardHeader>
                    <CardTitle className="text-purple-200 flex items-center justify-between">
                      <div className="flex items-center">
                        <Fish className="mr-2" size={20} />
                        {eventType}
                      </div>
                      <Badge className="bg-purple-700">{sessions.length} session(s)</Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {sessions.map(session => (
                      <div key={session.id} className="mb-4 last:mb-0 flex justify-between items-center bg-blue-900/20 p-3 rounded-lg border border-blue-700/20">
                        <div className="flex items-center gap-3">
                          <h4 className="text-purple-200 font-semibold">{session.name}</h4>
                          {session.status === 'closed' ? (
                            <Badge className="bg-purple-600"><Lock size={12} className="mr-1" />{t('closed') || 'Closed'}</Badge>
                          ) : (
                            <Badge className="bg-blue-600">{t('completed') || 'Completed'}</Badge>
                          )}
                        </div>
                        <Button
                          onClick={() => onFetchRunningOrder(session.id)}
                          size="sm"
                          className="bg-purple-600 hover:bg-purple-700 text-white"
                          data-testid={`view-rankings-${session.id}`}
                        >
                          <Trophy className="mr-2" size={14} />
                          {t('viewRankings') || 'View Rankings'}
                        </Button>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              ))}
            </div>
          );
        })()}

        {!selectedCompetition && (
          <Alert className="bg-blue-900/30 border-blue-700/50">
            <AlertDescription className="text-blue-200">
              {t('selectCompetitionToView') || 'Please select a competition to view results.'}
            </AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
};

export default EventResultsTab;
